import React from 'react'
import mapboxgl from 'mapbox-gl'
import PropTypes from 'prop-types'
import turfBbox from '@turf/bbox'
import { connect } from 'react-redux'
import { setMode } from '../../actions/mapSettings'
import { queryParcels } from './utils'

class BoxSelect extends React.Component {
    static propTypes = {
        selectParcels: PropTypes.func.isRequired,
        deselectParcels: PropTypes.func.isRequired,
        selectedParcels: PropTypes.arrayOf(PropTypes.string).isRequired,
        map: PropTypes.shape({
            dragPan: PropTypes.shape({
                enable: PropTypes.func,
                disable: PropTypes.func
            })
        }).isRequired
    }

    constructor(props) {
        super(props)

        this.canvas = null
        this.start = null
        this.current = null
        this.box = null
    }

    componentWillUnmount() {
        this.canvas.removeEventListener('mousedown', this.onMouseDown, true)
        document.removeEventListener('mousemove', this.onMouseMove)
        document.removeEventListener('mouseup', this.onMouseUp)
        document.removeEventListener('keydown', this.onKeyDown)
    }

    mousePos = e => {
        const rect = this.canvas.getBoundingClientRect()
        return new mapboxgl.Point(
            e.clientX - rect.left - this.canvas.clientLeft,
            e.clientY - rect.top - this.canvas.clientTop
        )
    }

    onMouseDown = e => {
        this.props.map.dragPan.disable()

        document.addEventListener('mousemove', this.onMouseMove)
        document.addEventListener('mouseup', this.onMouseUp)
        document.addEventListener('keydown', this.onKeyDown)

        this.start = this.mousePos(e)
    }

    onMouseMove = e => {
        this.current = this.mousePos(e)

        if (!this.box) {
            this.box = document.createElement('div')
            this.box.classList.add('boxdraw')
            this.canvas.appendChild(this.box)
        }

        const minX = Math.min(this.start.x, this.current.x)
        const maxX = Math.max(this.start.x, this.current.x)
        const minY = Math.min(this.start.y, this.current.y)
        const maxY = Math.max(this.start.y, this.current.y)

        const pos = `translate(${minX}px,${minY}px)`
        this.box.style.transform = pos
        this.box.style.WebkitTransform = pos
        this.box.style.width = `${maxX - minX}px`
        this.box.style.height = `${maxY - minY}px`
    }

    onMouseUp = e => {
        this.finish([this.start, this.mousePos(e)], !e.altKey)
    }

    onKeyDown = e => {
        if (e.code === 'Escape') this.finish()
    }

    finish = (bbox, isAdditive) => {
        document.removeEventListener('mousemove', this.onMouseMove)
        document.removeEventListener('mouseup', this.onMouseUp)
        document.removeEventListener('keydown', this.onKeyDown)

        if (this.box) {
            this.box.parentNode.removeChild(this.box)
            this.box = null
        }

        if (bbox) {
            let features = queryParcels(this.props.map, bbox)

            if (isAdditive) {
                // filter out already selected features
                features = features.filter(
                    feature => !this.props.selectedParcels.includes(feature.properties.parcel_apn)
                )
                this.props.selectParcels(
                    features.map(feature => {
                        const { properties } = feature
                        return {
                            id: properties.parcel_id,
                            apn: properties.parcel_apn,
                            county: properties.countyname.toLowerCase(),
                            address: {
                                address: properties.site_addr,
                                city: properties.site_city,
                                zip: properties.site_zip,
                                state: 'CA',
                                label: `${properties.site_addr}, ${properties.site_city} ${properties.site_zip}`
                            },
                            bounds: turfBbox(feature),
                            mpo: properties.mpo,
                            specific_plan: properties.specific_plan,
                            census_block: properties.census_block.split(' ').pop(),
                            zoning_designation: JSON.parse(properties.zoning_designation)
                        }
                    })
                )
            } else {
                // filter out currently unselected features
                features = features.filter(feature =>
                    this.props.selectedParcels.includes(feature.properties.parcel_apn)
                )
                this.props.deselectParcels(features.map(feature => feature.properties.parcel_apn))
            }
        }

        this.props.map.dragPan.enable()
    }

    update = map => {
        if (this.canvas === null) {
            this.canvas = map.getCanvasContainer()
            this.canvas.addEventListener('mousedown', this.onMouseDown, true)
        }
    }

    render() {
        if (this.props.map) {
            this.update(this.props.map)
        }
        return <></>
    }
}

export default connect(
    ({ parcels: { items } }) => ({
        selectedParcels: items.filter(item => item.type === 'parcel').map(item => item.apn)
    }),
    dispatch => {
        return { setMode: mode => dispatch(setMode(mode)) }
    }
)(BoxSelect)
